import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Gift } from "lucide-react";

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TermsModal({ isOpen, onClose }: TermsModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="text-center space-y-3">
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Gift className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-xl">Referral Program</DialogTitle>
          <p className="text-xs text-muted-foreground">
            Terms & Conditions · Last updated: June 2026
          </p>
        </DialogHeader>

        <div className="space-y-6 text-sm">
          <Section title="How It Works">
            <p className="text-muted-foreground leading-relaxed">
              Refer another gym owner to FirstRep and earn a credit on your
              account when they become a paying customer. The credit you earn is
              equal to the monthly plan value of the gym you referred.
            </p>
            <div className="mt-3 rounded-lg bg-muted/50 px-4 py-3 space-y-0.5">
              <p className="text-xs font-medium text-foreground">Example</p>
              <p className="text-muted-foreground leading-relaxed">
                If you refer a gym on our ₦17,000/month plan, you receive
                ₦17,000 off your next invoice — regardless of what plan you are
                on.
              </p>
            </div>
          </Section>

          <Section title="Earning Your Reward">
            <p className="text-muted-foreground leading-relaxed">
              To qualify for a referral credit:
            </p>
            <ul className="mt-2 space-y-2 text-muted-foreground">
              <ListItem>
                The gym you refer must be a new customer who has not previously
                used FirstRep
              </ListItem>
              <ListItem>
                They must complete onboarding and make their first payment
              </ListItem>
              <ListItem>
                They must mention your gym name when they get in touch with our
                team
              </ListItem>
            </ul>
            <p className="mt-3 text-muted-foreground">
              That's it. No links, no codes. Just have them mention your name.
            </p>
          </Section>

          <Section title="Receiving Your Credit">
            <div className="space-y-3 text-muted-foreground leading-relaxed">
              <p>
                Once the referred gym makes their first payment, our team will
                reach out to you to confirm your credit and arrange how it gets
                applied to your account. We aim to get in touch within{" "}
                <span className="font-medium text-foreground">
                  7 business days
                </span>
                .
              </p>
              <p>
                Since we work with gym owners directly, we'll agree with you on
                the best way to apply your credit based on your payment setup —
                this may mean a reduced invoice on your next payment, an
                extended membership period, or another arrangement that works
                for you.
              </p>
              <p>
                Credit is applied once that conversation is complete and
                confirmed, so response times on your end can affect when it
                lands.
              </p>
            </div>
          </Section>

          <Section title="A Few Things to Note">
            <ul className="space-y-2 text-muted-foreground">
              <ListItem>
                There is no limit on the number of gyms you can refer — each
                successful referral earns you a separate credit
              </ListItem>
              <ListItem>Credits cannot be exchanged for cash</ListItem>
              <ListItem>
                The referred gym must remain a paying customer at the time the
                credit is applied. If they cancel before their first payment is
                confirmed, the credit will not be issued
              </ListItem>
              <ListItem>
                Credits are non-transferable and tied to your account only
              </ListItem>
              <ListItem>
                FirstRep reserves the right to modify or discontinue this
                program at any time. Any credits already earned will still be
                honoured
              </ListItem>
            </ul>
          </Section>

          <div className="rounded-lg border border-border/60 bg-muted/30 px-4 py-3 space-y-1">
            <p className="font-semibold text-foreground text-sm">Questions?</p>
            <p className="text-muted-foreground leading-relaxed">
              Reach out to us at{" "}
              <a
                href="mailto:koya@firstrep.xyz"
                className="text-primary underline-offset-4 hover:underline"
              >
                koya@firstrep.xyz
              </a>{" "}
              or on WhatsApp and we'll sort it out.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-foreground">{title}</h3>
      {children}
    </div>
  );
}

function ListItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
      <span className="leading-relaxed">{children}</span>
    </li>
  );
}
